const DROP_ENTIRELY_TAGS = new Set([
  'base',
  'head',
  'iframe',
  'link',
  'math',
  'meta',
  'noscript',
  'object',
  'script',
  'style',
  'svg',
  'template',
  'title',
]);

interface SanitizeAllowlistHtmlOptions {
  allowedTags: readonly string[];
  preserveLineBreaks?: boolean;
}

export function sanitizeAllowlistHtml(
  value: string | null | undefined,
  { allowedTags, preserveLineBreaks = false }: SanitizeAllowlistHtmlOptions
): string {
  if (value == null || value === '') {
    return '';
  }

  const parser = new DOMParser();
  const sourceDocument = parser.parseFromString(String(value), 'text/html');
  const targetDocument = parser.parseFromString('', 'text/html');
  const container = targetDocument.createElement('div');
  const allowedTagsSet = new Set(allowedTags.map(tagName => tagName.toLowerCase()));

  for (const childNode of Array.from(sourceDocument.body.childNodes)) {
    appendSanitizedNodes(childNode, container, targetDocument, allowedTagsSet);
  }

  const sanitizedContent = container.innerHTML;

  if (!preserveLineBreaks) {
    return sanitizedContent;
  }

  return sanitizedContent.replace(/(?:\r\n|\r|\n)/g, '<br>');
}

function appendSanitizedNodes(
  sourceNode: Node,
  targetParent: Node,
  targetDocument: Document,
  allowedTags: ReadonlySet<string>
): void {
  if (sourceNode.nodeType === Node.TEXT_NODE) {
    targetParent.appendChild(targetDocument.createTextNode(sourceNode.textContent ?? ''));
    return;
  }

  if (sourceNode.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const sourceElement = sourceNode as Element;
  const tagName = sourceElement.tagName.toLowerCase();

  if (DROP_ENTIRELY_TAGS.has(tagName)) {
    return;
  }

  if (allowedTags.has(tagName)) {
    const sanitizedElement = targetDocument.createElement(tagName);

    for (const childNode of Array.from(sourceElement.childNodes)) {
      appendSanitizedNodes(childNode, sanitizedElement, targetDocument, allowedTags);
    }

    targetParent.appendChild(sanitizedElement);
    return;
  }

  for (const childNode of Array.from(sourceElement.childNodes)) {
    appendSanitizedNodes(childNode, targetParent, targetDocument, allowedTags);
  }
}
